import React, { useId, useMemo } from 'react';
import { motion } from 'motion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList, faTableCells } from '@fortawesome/free-solid-svg-icons';
import {
  FORECAST_CHART,
  FORECAST_COLORS,
  FORECAST_PATHS,
  FORECAST_SCENARIO_ORDER,
  FORECAST_SCENARIOS,
} from '../heroTheaterData';

interface ForecastSceneProps {
  progress: number;
  reducedMotion: boolean;
}

/** Fixed aspect on sm+; on mobile the plot fills the panel height. */
const VB = { w: 148, h: 88 } as const;
/** Kept tight to the viewBox edges so the plot doesn’t sit in empty side gutters. */
const CREATED_X = 10;
/** Farther right so ~6mo of history (black stem) reads longer before the fan. */
const NOW_X = 42;
/** Tuck projection strokes slightly into history so the Now hub reads as one continuous fork. */
const JOIN_OVERLAP = 3.2;
const END_X = 118;
const AXIS_Y = 84;
const PRICE_TOP = 12;
const PRICE_BOT = 80;
const PRICE_MIN = 220;
const PRICE_MAX = 2300;

function clamp01(n: number) {
  return Math.min(1, Math.max(0, n));
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function priceToY(price: number) {
  return PRICE_TOP + (1 - (price - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * (PRICE_BOT - PRICE_TOP);
}

function smoothPath(points: { x: number; y: number }[]) {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
  let d = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i];
    const p1 = points[i + 1];
    const dx = p1.x - p0.x;
    d += ` C ${(p0.x + dx * 0.4).toFixed(1)} ${p0.y.toFixed(1)} ${(p1.x - dx * 0.4).toFixed(1)} ${p1.y.toFixed(1)} ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}`;
  }
  return d;
}

/** Closed ribbon between an upper and lower series (possibility cone). */
function areaBetween(upper: { x: number; y: number }[], lower: { x: number; y: number }[]) {
  if (upper.length === 0 || lower.length === 0) return '';
  const forward = smoothPath(upper);
  let d = forward;
  const rev = [...lower].reverse();
  d += ` L ${rev[0].x.toFixed(1)} ${rev[0].y.toFixed(1)}`;
  for (let i = 0; i < rev.length - 1; i++) {
    const p0 = rev[i];
    const p1 = rev[i + 1];
    const dx = p1.x - p0.x;
    d += ` C ${(p0.x + dx * 0.4).toFixed(1)} ${p0.y.toFixed(1)} ${(p1.x - dx * 0.4).toFixed(1)} ${p1.y.toFixed(1)} ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}`;
  }
  return `${d} Z`;
}

/** Soft fill under a series down to the axis. */
function areaUnder(points: { x: number; y: number }[]) {
  if (points.length === 0) return '';
  const line = smoothPath(points);
  const last = points[points.length - 1];
  const first = points[0];
  return `${line} L ${last.x.toFixed(1)} ${AXIS_Y} L ${first.x.toFixed(1)} ${AXIS_Y} Z`;
}

function scaleSeries(
  samples: readonly { t: number; price: number }[],
  boostT: number,
  boostedEnd: number,
) {
  const start = samples[0].price;
  const oldEnd = samples[samples.length - 1].price;
  const newEnd = lerp(oldEnd, boostedEnd, boostT);
  const span = oldEnd - start || 1;
  return samples.map((s) => ({
    t: s.t,
    price: start + ((s.price - start) / span) * (newEnd - start),
  }));
}

/**
 * Keep scenarios glued near "Now" for a short neck, then fan —
 * avoids three streaks that look pasted on after the historical ink.
 */
function withStemNeck(samples: readonly { t: number; price: number }[]) {
  if (samples.length < 2) return [...samples];
  const start = samples[0];
  const next = samples[1];
  const neck = {
    t: lerp(start.t, next.t, 0.38),
    price: lerp(start.price, next.price, 0.16),
  };
  return [start, neck, ...samples.slice(1)];
}

/** Interpolate a point along a normalized series at draw progress 0–1. */
function tipAt(
  samples: readonly { t: number; price: number }[],
  draw: number,
  x0: number,
  x1: number,
) {
  const t = clamp01(draw);
  if (t <= 0) {
    return { x: x0 + samples[0].t * (x1 - x0), y: priceToY(samples[0].price) };
  }
  if (t >= 1) {
    const last = samples[samples.length - 1];
    return { x: x0 + last.t * (x1 - x0), y: priceToY(last.price) };
  }
  let i = 0;
  while (i < samples.length - 1 && samples[i + 1].t < t) i += 1;
  const a = samples[i];
  const b = samples[Math.min(i + 1, samples.length - 1)];
  const span = b.t - a.t || 1;
  const local = clamp01((t - a.t) / span);
  const price = lerp(a.price, b.price, local);
  const x = x0 + lerp(a.t, b.t, local) * (x1 - x0);
  return { x, y: priceToY(price) };
}

function formatUsd(n: number) {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

function formatPct(n: number) {
  const rounded = Math.round(n);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

function Stepper({
  value,
  unit,
  borderClass,
  pulsePlus,
  reducedMotion,
}: {
  value: number;
  unit: string;
  borderClass: string;
  pulsePlus: boolean;
  reducedMotion: boolean;
}) {
  return (
    <div className="inline-flex items-center justify-center gap-0.5 sm:gap-1">
      <span
        className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border bg-white text-[8px] font-bold leading-none text-gray-500 sm:h-4 sm:w-4 sm:text-[9px] ${borderClass}`}
      >
        −
      </span>
      <span className="min-w-[1.6rem] text-center text-[8px] font-semibold tabular-nums text-gray-900 sm:min-w-[1.85rem] sm:text-[9px] md:text-[10px]">
        {value}
        <span className="ml-0.5 font-medium text-gray-400">{unit}</span>
      </span>
      <motion.span
        className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border bg-white text-[8px] font-bold leading-none text-gray-500 sm:h-4 sm:w-4 sm:text-[9px] ${borderClass}`}
        animate={reducedMotion || !pulsePlus ? { scale: 1 } : { scale: [1, 1.16, 1] }}
        transition={
          pulsePlus && !reducedMotion
            ? { duration: 0.45, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.15 }
        }
      >
        +
      </motion.span>
    </div>
  );
}

/**
 * Price Forecast panel: historical path, three scenario projections, then a CAGR nudge.
 */
export default function ForecastScene({ progress, reducedMotion }: ForecastSceneProps) {
  // Beat map (chapter ~6.6s — readable, not lingering):
  // 0–0.08: chrome
  // 0.08–0.18: created → now
  // 0.18–0.48: bull / base / bear paths (staggered)
  // 0.45–0.55: endpoint labels
  // 0.52–0.70: table
  // 0.76–1.0: CAGR + futures lift
  const histOn = reducedMotion || progress > 0.06;
  const markersOn = reducedMotion || progress > 0.14;
  const projPhase = reducedMotion ? 1 : clamp01((progress - 0.18) / 0.24);
  const endsOn = reducedMotion || progress > 0.4;
  const tablePhase = reducedMotion ? 1 : clamp01((progress - 0.48) / 0.16);
  const boostT = reducedMotion ? 0 : clamp01((progress - 0.74) / 0.18);
  const pulsing = !reducedMotion && boostT > 0 && boostT < 1;
  const coneOn = reducedMotion ? 1 : clamp01((projPhase - 0.15) / 0.7);

  const uid = useId().replace(/:/g, '');
  const histClip = `hero-fc-hist-${uid}`;
  const coneClip = `hero-fc-cone-${uid}`;
  const gridId = `hero-fc-grid-${uid}`;
  const histFill = `hero-fc-hist-fill-${uid}`;
  const coneFill = `hero-fc-cone-fill-${uid}`;
  const nowGlow = `hero-fc-now-glow-${uid}`;
  const projClips = FORECAST_SCENARIO_ORDER.map((key) => `hero-fc-proj-${uid}-${key}`);

  const scenarios = useMemo(
    () =>
      FORECAST_SCENARIO_ORDER.map((key) => {
        const s = FORECAST_SCENARIOS[key];
        return {
          ...s,
          price: lerp(s.price, s.boostedPrice, boostT),
          growth: lerp(s.growth, s.boostedGrowth, boostT),
          revenueB: lerp(s.revenueB, s.boostedRevenueB, boostT),
          marketCapT: lerp(s.marketCapT, s.boostedMarketCapT, boostT),
          cagr: Math.round(lerp(s.cagr, s.boostedCagr, boostT)),
          samples: withStemNeck(scaleSeries(FORECAST_PATHS[key], boostT, s.boostedPrice)),
        };
      }),
    [boostT],
  );

  const histPts = useMemo(
    () =>
      FORECAST_PATHS.historical.map((s) => ({
        x: CREATED_X + s.t * (NOW_X - CREATED_X),
        y: priceToY(s.price),
      })),
    [],
  );
  const histPath = useMemo(() => smoothPath(histPts), [histPts]);
  const histArea = useMemo(() => areaUnder(histPts), [histPts]);

  const scenarioPts = useMemo(
    () =>
      scenarios.map((s) => {
        const mapped = s.samples.map((pt) => ({
          x: NOW_X + pt.t * (END_X - NOW_X),
          y: priceToY(pt.price),
        }));
        // Overlap into history so color strokes meet the black ink under the Now hub
        return [
          { x: NOW_X - JOIN_OVERLAP, y: priceToY(FORECAST_CHART.nowPrice) },
          ...mapped,
        ];
      }),
    [scenarios],
  );

  const projPaths = useMemo(() => scenarioPts.map((pts) => smoothPath(pts)), [scenarioPts]);

  const conePath = useMemo(() => {
    const bull = scenarioPts[0];
    const bear = scenarioPts[2];
    if (!bull || !bear) return '';
    return areaBetween(bull, bear);
  }, [scenarioPts]);

  const createdY = priceToY(FORECAST_CHART.createdPrice);
  const nowY = priceToY(FORECAST_CHART.nowPrice);

  /** Stagger each scenario so the fan opens with intent. */
  const scenarioDraw = (index: number) =>
    reducedMotion ? 1 : clamp01((projPhase - index * 0.1) / 0.52);

  const projRevealX0 = NOW_X - JOIN_OVERLAP;
  const projRevealMaxW = END_X - projRevealX0 + 6;

  const tableRows = [
    {
      id: 'rev',
      label: (
        <>
          Revenue <span className="font-normal text-slate-400">({FORECAST_CHART.endYear})</span>
        </>
      ),
      cells: scenarios.map((s) => ({
        key: s.key,
        className: s.textClass,
        content: `$${s.revenueB.toFixed(1)}B`,
      })),
    },
    {
      id: 'mcap',
      label: (
        <>
          Market Cap <span className="font-normal text-slate-400">({FORECAST_CHART.endYear})</span>
        </>
      ),
      cells: scenarios.map((s) => ({
        key: s.key,
        className: s.textClass,
        content: `$${s.marketCapT.toFixed(2)}T`,
      })),
    },
  ] as const;

  return (
    <div className="relative flex h-full w-full min-h-0 flex-col overflow-hidden bg-background px-1.5 pb-2.5 pt-7 sm:px-3.5 sm:pb-3 sm:pt-8 md:px-4 md:pb-3 md:pt-3">
      {/* Chart — full-bleed on mobile (cancel scene pad); plot fills leftover space */}
      <motion.div
        className="relative -mx-1.5 flex min-h-0 flex-[1.5] flex-col overflow-hidden rounded-none pb-2 pl-2 pr-0.5 pt-2 ring-1 ring-black/[0.05] sm:mx-0 sm:flex-[1.25] sm:rounded-xl sm:px-2.5 sm:pb-2.5 sm:pt-3 md:flex-[1.45] md:px-3 md:pt-3"
        style={{
          background:
            'radial-gradient(120% 90% at 18% 20%, rgba(253,230,138,0.14) 0%, transparent 42%), radial-gradient(90% 80% at 88% 18%, rgba(167,243,208,0.12) 0%, transparent 48%), radial-gradient(80% 70% at 92% 78%, rgba(254,215,170,0.1) 0%, transparent 50%), linear-gradient(180deg, #fafaf7 0%, #f5f5f0 100%)',
        }}
        initial={reducedMotion ? false : { opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
      >
        {/* Soft color wash that intensifies as scenarios open */}
        <motion.div
          className="pointer-events-none absolute inset-0"
          initial={false}
          animate={{ opacity: 0.2 + coneOn * 0.28 + boostT * 0.12 }}
          aria-hidden
          style={{
            background:
              'linear-gradient(105deg, transparent 28%, rgba(16,185,129,0.045) 48%, rgba(37,99,235,0.04) 62%, rgba(234,88,12,0.045) 78%, transparent 92%)',
          }}
        />

        <div className="relative z-[1] mb-1 flex shrink-0 justify-center sm:mb-1.5 md:mb-0.5">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-emerald-700 sm:text-base">
            Forecast
          </p>
        </div>

        {/*
          Plot stage: on desktop the tablet frame is landscape — size from height
          at the viewBox aspect so the fan isn’t stretched flat. Avoid w-max +
          aspect-ratio sizing — that circular layout can delay first paint until
          the reveal has already finished.
        */}
        <div className="relative z-[1] flex min-h-0 w-full flex-1 justify-center md:items-center">
          <div className="flex h-full min-h-0 w-full flex-col md:aspect-[148/88] md:h-full md:w-auto md:max-w-full">
            <div className="relative min-h-0 w-full flex-1">
              <svg
                viewBox={`0 0 ${VB.w} ${VB.h}`}
                className="absolute inset-0 h-full w-full"
                preserveAspectRatio="none"
                aria-hidden
              >
            <defs>
              <pattern id={gridId} width="12" height="10" patternUnits="userSpaceOnUse">
                <path
                  d="M 12 0 L 0 0 0 10"
                  fill="none"
                  stroke="rgba(15,23,42,0.045)"
                  strokeWidth="0.4"
                />
              </pattern>

              <linearGradient id={histFill} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0f172a" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#0f172a" stopOpacity="0" />
              </linearGradient>

              <linearGradient id={coneFill} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#16a34a" stopOpacity="0.28" />
                <stop offset="45%" stopColor="#2563eb" stopOpacity="0.16" />
                <stop offset="100%" stopColor="#ea580c" stopOpacity="0.22" />
              </linearGradient>

              <radialGradient id={nowGlow} cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
              </radialGradient>

              <clipPath id={histClip}>
                <motion.rect
                  x="0"
                  y="0"
                  height={VB.h}
                  initial={false}
                  animate={{ width: histOn ? NOW_X + JOIN_OVERLAP * 0.35 : 0 }}
                  transition={{ duration: reducedMotion ? 0 : 0.32, ease: 'easeOut' }}
                />
              </clipPath>

              <clipPath id={coneClip}>
                <motion.rect
                  x={projRevealX0}
                  y="0"
                  height={VB.h}
                  initial={false}
                  animate={{
                    width: reducedMotion
                      ? projRevealMaxW
                      : Math.max(scenarioDraw(0), scenarioDraw(2)) * projRevealMaxW,
                  }}
                  transition={{ duration: reducedMotion ? 0 : 0.05, ease: 'linear' }}
                />
              </clipPath>

              {FORECAST_SCENARIO_ORDER.map((key, i) => (
                <clipPath key={key} id={projClips[i]}>
                  <motion.rect
                    x={projRevealX0}
                    y="0"
                    height={VB.h}
                    initial={false}
                    animate={{ width: scenarioDraw(i) * projRevealMaxW }}
                    transition={{ duration: reducedMotion ? 0 : 0.05, ease: 'linear' }}
                  />
                </clipPath>
              ))}
            </defs>

            <rect x="0" y="0" width={VB.w} height={VB.h} fill={`url(#${gridId})`} opacity="0.9" />

            {/* Quiet horizontal guide */}
            <line
              x1={CREATED_X}
              x2={END_X + 2}
              y1={AXIS_Y}
              y2={AXIS_Y}
              stroke="#d6d3d1"
              strokeWidth="0.5"
              vectorEffect="non-scaling-stroke"
            />

            {/* Possibility cone — continuous fill between bull and bear (no banding) */}
            <g clipPath={`url(#${coneClip})`}>
              <path
                d={conePath}
                fill={`url(#${coneFill})`}
                opacity={0.7 + boostT * 0.2}
              />
            </g>

            <motion.line
              x1={CREATED_X}
              x2={CREATED_X}
              y1={createdY}
              y2={AXIS_Y}
              stroke="#a8a29e"
              strokeWidth="0.4"
              strokeDasharray="1.2 1.6"
              vectorEffect="non-scaling-stroke"
              initial={false}
              animate={{ opacity: markersOn ? 0.85 : 0 }}
            />

            {/* Now divider — sits under the price join so it doesn't sever the fork */}
            <motion.line
              x1={NOW_X}
              x2={NOW_X}
              y1={nowY + 3.5}
              y2={AXIS_Y}
              stroke={FORECAST_COLORS.now}
              strokeWidth="0.55"
              strokeDasharray="1.3 1.5"
              vectorEffect="non-scaling-stroke"
              initial={false}
              animate={{ opacity: markersOn ? 0.55 : 0 }}
            />
            <motion.line
              x1={NOW_X}
              x2={NOW_X}
              y1={nowY + 3.5}
              y2={AXIS_Y}
              stroke="#fde68a"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
              initial={false}
              animate={{ opacity: markersOn ? 0.14 : 0 }}
            />

            {/* Historical area + path */}
            <g clipPath={`url(#${histClip})`}>
              <path d={histArea} fill={`url(#${histFill})`} />
              <path
                d={histPath}
                fill="none"
                stroke="#0f172a"
                strokeWidth="4.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.12"
                vectorEffect="non-scaling-stroke"
              />
              <path
                d={histPath}
                fill="none"
                stroke="#0f172a"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </g>

            {/* Solid projection strokes — clip reveal (no dash gaps) */}
            {scenarios.map((s, i) => (
              <g key={s.key} clipPath={`url(#${projClips[i]})`}>
                <path
                  d={projPaths[i]}
                  fill="none"
                  stroke={s.color}
                  strokeWidth="4.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.2"
                  vectorEffect="non-scaling-stroke"
                />
                <path
                  d={projPaths[i]}
                  fill="none"
                  stroke={s.color}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            ))}

            {/* Traveling ink tips while paths draw */}
            {!reducedMotion &&
              scenarios.map((s, i) => {
                const draw = scenarioDraw(i);
                if (draw <= 0.02 || draw >= 0.98) return null;
                const tip = tipAt(s.samples, draw, NOW_X, END_X);
                return (
                  <g key={`${s.key}-tip`}>
                    <circle cx={tip.x} cy={tip.y} r="3.2" fill={s.color} opacity="0.2" />
                    <circle
                      cx={tip.x}
                      cy={tip.y}
                      r="1.55"
                      fill={s.color}
                      stroke="#f7f7f2"
                      strokeWidth="0.7"
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                );
              })}

            {/* Created marker */}
            <motion.circle
              cx={CREATED_X}
              cy={createdY}
              r="1.7"
              fill={FORECAST_COLORS.created}
              stroke="#f7f7f2"
              strokeWidth="0.95"
              vectorEffect="non-scaling-stroke"
              initial={false}
              animate={{ opacity: markersOn ? 1 : 0 }}
            />

            {/* Now marker — hub for the fork, kept tight so it doesn't erase the join */}
            <motion.circle
              cx={NOW_X}
              cy={nowY}
              fill={`url(#${nowGlow})`}
              initial={false}
              animate={{
                opacity: markersOn ? 1 : 0,
                r: reducedMotion || !markersOn ? 4.4 : [4.2, 5.6, 4.2],
              }}
              transition={
                markersOn && !reducedMotion
                  ? { opacity: { duration: 0.2 }, r: { duration: 1.8, repeat: Infinity, ease: 'easeInOut' } }
                  : { duration: 0.2 }
              }
            />
            <motion.circle
              cx={NOW_X}
              cy={nowY}
              r="2.15"
              fill="#92400e"
              stroke="#fef3c7"
              strokeWidth="0.95"
              vectorEffect="non-scaling-stroke"
              initial={false}
              animate={{ opacity: markersOn ? 1 : 0 }}
            />

            {/* Endpoint orbs */}
            {scenarios.map((s, i) => {
              const ready = endsOn && scenarioDraw(i) > 0.85;
              const ey = priceToY(s.price);
              return (
                <g key={`${s.key}-end`}>
                  <motion.circle
                    cx={END_X}
                    cy={ey}
                    fill={s.color}
                    initial={false}
                    animate={{ opacity: ready ? 0.22 : 0, r: ready ? 4.4 : 1.5 }}
                    transition={{ duration: 0.28 }}
                  />
                  <motion.circle
                    cx={END_X}
                    cy={ey}
                    fill={s.color}
                    stroke="#f7f7f2"
                    strokeWidth="0.95"
                    vectorEffect="non-scaling-stroke"
                    initial={false}
                    animate={{ opacity: ready ? 1 : 0, r: ready ? 2.1 : 0.8 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 22 }}
                  />
                </g>
              );
            })}
          </svg>

          {/* Price tags at Created / Now */}
          <motion.div
            className="pointer-events-none absolute inset-0 z-[1]"
            initial={false}
            animate={{ opacity: markersOn ? 1 : 0 }}
          >
            <span
              className="absolute -translate-x-1/2 -translate-y-full rounded-md bg-slate-900 px-1.5 py-0.5 text-[9px] font-bold tabular-nums leading-none text-white shadow-sm sm:text-[9px] md:text-[10px]"
              style={{
                left: `${(CREATED_X / VB.w) * 100}%`,
                top: `${((createdY - 3.2) / VB.h) * 100}%`,
              }}
            >
              {FORECAST_CHART.createdTag}
            </span>
            <span
              className="absolute -translate-x-1/2 -translate-y-full rounded-md bg-amber-300 px-1.5 py-0.5 text-[9px] font-bold tabular-nums leading-none text-amber-950 shadow-sm shadow-amber-700/15 sm:text-[9px] md:text-[10px]"
              style={{
                left: `${(NOW_X / VB.w) * 100}%`,
                top: `${((nowY - 3.5) / VB.h) * 100}%`,
              }}
            >
              {FORECAST_CHART.nowTag}
            </span>
          </motion.div>

          {/* Endpoint scenario labels — the aha readout */}
          <motion.div
            className="pointer-events-none absolute inset-0 z-[2] overflow-hidden"
            initial={false}
            animate={{ opacity: endsOn ? 1 : 0 }}
            transition={{ duration: 0.22 }}
          >
            {scenarios.map((s, i) => {
              const y = priceToY(s.price);
              const yPct = (y / VB.h) * 100;
              const topPct = Math.max(yPct, 17);
              const visible = scenarioDraw(i) > 0.8;
              return (
                <motion.div
                  key={`${s.key}-label`}
                  className="absolute flex -translate-y-1/2 flex-col items-start leading-none"
                  style={{
                    left: `${((END_X + 3.5) / VB.w) * 100}%`,
                    top: `${topPct}%`,
                  }}
                  initial={false}
                  animate={{
                    opacity: visible ? 1 : 0,
                    x: visible ? 0 : 6,
                  }}
                  transition={{ duration: 0.22 }}
                >
                  <span
                    className={`text-[8px] font-bold uppercase tracking-[0.08em] sm:text-[8px] md:text-[9px] ${s.textClass}`}
                  >
                    {s.label}
                  </span>
                  <span className="mt-0.5 text-[10px] font-bold tabular-nums tracking-tight text-slate-900 sm:text-[10px] md:text-[11px]">
                    {formatUsd(s.price)}
                  </span>
                  <span className={`mt-px text-[8px] font-semibold tabular-nums sm:text-[8px] ${s.textClass}`}>
                    {formatPct(s.growth)}
                  </span>
                </motion.div>
              );
            })}
            </motion.div>
            </div>

            {/* Axis captions — same width as plot so marks stay aligned */}
            <motion.div
              className="relative mt-0 h-6 shrink-0 sm:h-7 md:h-5"
              initial={false}
              animate={{ opacity: histOn ? 1 : 0 }}
            >
              <div
                className="absolute top-0 flex -translate-x-1/2 flex-col items-center leading-tight"
                style={{ left: `${(CREATED_X / VB.w) * 100}%` }}
              >
                <span className="text-[8px] font-medium text-slate-500 sm:text-[9px] md:text-[10px]">
                  Created
                </span>
                <span className="text-[8px] font-medium text-slate-500 sm:text-[9px] md:text-[10px]">
                  {FORECAST_CHART.createdCaption}
                </span>
              </div>
              <span
                className="absolute top-0 -translate-x-1/2 text-[9px] font-semibold leading-none text-amber-800/90 sm:text-[10px] md:text-[11px]"
                style={{ left: `${(NOW_X / VB.w) * 100}%` }}
              >
                Now
              </span>
              <span
                className="absolute top-0 -translate-x-1/2 text-[9px] font-semibold tabular-nums leading-none text-slate-600 sm:text-[10px] md:text-[11px]"
                style={{ left: `${(END_X / VB.w) * 100}%` }}
              >
                {FORECAST_CHART.endYear}
              </span>
            </motion.div>
          </div>
        </div>

        <div className="relative z-[3] mt-1.5 flex shrink-0 justify-center sm:mt-2 md:mt-1">
          <div className="flex items-center justify-center gap-2.5 sm:gap-3">
            <span className="text-[10px] font-medium tracking-tight text-slate-500 sm:text-[10px] md:text-[11px]">
              <span className="font-semibold tabular-nums text-slate-800">{FORECAST_CHART.years}</span>
              <span className="ml-1">yr projection</span>
            </span>
            <div className="relative h-2 w-[68px] rounded-full bg-stone-200/90 sm:h-2.5 sm:w-[88px] md:w-[100px]">
              <div className="absolute inset-y-0 left-0 w-[44%] rounded-full bg-gradient-to-r from-slate-700 to-slate-900" />
              <div className="absolute left-[44%] top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-md ring-1 ring-black/10 sm:h-4 sm:w-4" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Scenario math — supporting, not competing */}
      <div className="mt-2 flex min-h-0 flex-1 flex-col sm:mt-2.5 md:mt-2 md:flex-[0.9]">
        <motion.div
          className="mb-1.5 flex shrink-0 items-center justify-between gap-2"
          initial={false}
          animate={{ opacity: tablePhase > 0.05 ? 1 : 0 }}
        >
          <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
            <span className="text-[8px] font-semibold text-slate-600 sm:text-[9px] md:text-[10px]">
              Projection by
            </span>
            <span className="inline-flex items-center rounded-md border border-slate-200 bg-white p-0.5">
              <span className="rounded-[5px] bg-slate-900 px-1.5 py-0.5 text-[7px] font-bold text-white sm:px-2 sm:text-[8px]">
                P/S
              </span>
              <span className="px-1.5 py-0.5 text-[7px] font-medium text-slate-400 sm:px-2 sm:text-[8px]">
                P/E
              </span>
            </span>
          </div>
          <div className="hidden items-center gap-2.5 sm:flex md:gap-3">
            <span className="inline-flex items-center gap-1 text-[8px] font-medium text-slate-500 md:text-[9px]">
              <FontAwesomeIcon icon={faList} className="h-[0.75em] w-[0.75em]" />
              Key Stats
            </span>
            <span className="inline-flex items-center gap-1 text-[8px] font-medium text-slate-500 md:text-[9px]">
              <FontAwesomeIcon icon={faTableCells} className="h-[0.75em] w-[0.75em]" />
              Financials
            </span>
          </div>
        </motion.div>

        <motion.div
          className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200/80 bg-white"
          initial={false}
          animate={{ opacity: tablePhase > 0.12 ? 1 : 0, y: tablePhase > 0.12 ? 0 : 6 }}
          transition={{ duration: 0.32 }}
        >
          <table className="h-full w-full table-fixed border-separate border-spacing-0 text-left">
            <thead>
              <tr className="h-[18%]">
                <th className="w-[28%] border-b border-slate-100 px-2 py-0 sm:w-[26%] sm:px-2.5" />
                {scenarios.map((s) => (
                  <th
                    key={s.key}
                    className="border-b border-slate-100 px-0.5 py-0 text-center align-middle sm:px-1"
                  >
                    <span
                      className={`inline-flex items-center justify-center gap-1 text-[8px] font-bold sm:text-[9px] md:text-[10px] ${s.textClass}`}
                    >
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: s.color }}
                      />
                      {s.label}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr key={row.id} className="h-[20.5%]">
                  <td className="border-b border-slate-100 px-2 py-0 align-middle text-[7px] font-medium text-slate-600 sm:px-2.5 sm:text-[8px] md:text-[9px]">
                    {row.label}
                  </td>
                  {row.cells.map((cell) => (
                    <td
                      key={cell.key}
                      className={`border-b border-slate-100 px-0.5 py-0 text-center align-middle text-[8px] font-semibold tabular-nums sm:text-[9px] md:text-[10px] ${cell.className}`}
                    >
                      {cell.content}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="h-[20.5%]">
                <td className="border-b border-slate-100 px-2 py-0 align-middle text-[7px] font-medium text-slate-600 sm:px-2.5 sm:text-[8px] md:text-[9px]">
                  Revenue CAGR
                </td>
                {scenarios.map((s) => (
                  <td key={s.key} className="border-b border-slate-100 px-0.5 py-0 text-center align-middle">
                    <Stepper
                      value={s.cagr}
                      unit="%"
                      borderClass={s.borderClass}
                      pulsePlus={pulsing}
                      reducedMotion={reducedMotion}
                    />
                  </td>
                ))}
              </tr>
              <tr className="h-[20.5%]">
                <td className="px-2 py-0 align-middle text-[7px] font-medium text-slate-600 sm:px-2.5 sm:text-[8px] md:text-[9px]">
                  P/S multiple
                </td>
                {scenarios.map((s) => (
                  <td key={s.key} className="px-0.5 py-0 text-center align-middle">
                    <Stepper
                      value={s.multiple}
                      unit="x"
                      borderClass={s.borderClass}
                      pulsePlus={false}
                      reducedMotion={reducedMotion}
                    />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </motion.div>
      </div>
    </div>
  );
}
